import React, { useEffect, useState } from "react";
import { api } from "../api";
import { errText } from "./ClientAuth";

const STATUS = {
  paired: ["✓ Appairé", "text-emerald-300 bg-emerald-500/10"],
  pending: ["En attente", "text-amber-300 bg-amber-500/10"],
};

const Steps = ({ account }) => (
  <ol className="list-decimal ml-4 space-y-1.5 text-xs text-zinc-300" data-testid="client-pairing-steps">
    <li>MT5 → <b>Outils → Options → Expert Advisors</b> : cochez « Autoriser WebRequest pour les URL listées » et ajoutez
      <span className="font-mono text-cyan-300"> https://drtradebots.com</span></li>
    <li>Ouvrez les paramètres de l'EA sur le compte <span className="font-mono">{account}</span>, rubrique Analytics :
      <span className="font-mono"> Analytics_Enable = true</span> et <span className="font-mono">Analytics_API_Token</span> = le token ci-dessus</li>
    <li>Optionnel : <span className="font-mono">Analytics_Rebuild_History_On_Init = true</span> pour envoyer les 90 derniers jours</li>
    <li>Validez (l'EA redémarre). Le journal « Experts » affiche « Connexion au site OK ».</li>
    <li>Revenez ici et cliquez sur <b>Pairing</b>.</li>
  </ol>
);

const STALE_MS = 60 * 60 * 1000;

// Marché Forex fermé (ven. 21h → dim. 22h UTC) : l'EA ne reçoit pas de ticks, pas d'alerte
const marketClosed = (d = new Date()) => {
  const day = d.getUTCDay(), h = d.getUTCHours();
  return (day === 5 && h >= 21) || day === 6 || (day === 0 && h < 22);
};

export const staleAccounts = (accounts) => marketClosed() ? [] : accounts.filter(
  (a) => a.status === "paired" && (!a.last_seen_at || Date.now() - new Date(a.last_seen_at).getTime() > STALE_MS));

const sinceLabel = (iso) => {
  if (!iso) return "jamais";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return min < 120 ? `il y a ${min} min` : min < 2880 ? `il y a ${Math.floor(min / 60)} h` : `il y a ${Math.floor(min / 1440)} j`;
};

const DisconnectedAlert = ({ accounts }) => {
  const stale = staleAccounts(accounts);
  if (!stale.length) return null;
  return (
    <div className="rounded-xl p-4 mb-4 border border-amber-500/40 bg-amber-500/10 text-xs text-amber-100" data-testid="client-ea-disconnected-alert">
      <div className="font-semibold text-sm text-amber-300 mb-1">⚠️ EA déconnecté</div>
      {stale.map((a) => (
        <div key={a.account} data-testid={`client-ea-disconnected-${a.account}`}>
          Compte <span className="font-mono">{a.account}</span> : aucune connexion de l'EA depuis plus d'une heure (dernière : {sinceLabel(a.last_seen_at)}).
        </div>
      ))}
      <div className="text-amber-200/80 mt-1.5">
        Vérifiez que MT5 est ouvert et connecté, que l'EA est actif sur le graphique, que Analytics_Enable = true et que WebRequest autorise https://drtradebots.com.
        Les trades fermés pendant la coupure seront envoyés au redémarrage si Analytics_Rebuild_History_On_Init = true.
      </div>
    </div>
  );
};

/** Numéro de compte → token API + réglages MT5 → bouton Pairing (test de connexion réel de l'EA). */
export const PairingPanel = ({ headers, onLinked }) => {
  const [accounts, setAccounts] = useState([]);
  const [value, setValue] = useState("");
  const [pairing, setPairing] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = () => api.get("/client/analytics/accounts", headers).then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const call = async (what, fn) => {
    setBusy(what); setErr("");
    try { await fn(); } catch (e) { setErr(errText(e)); } finally { setBusy(""); }
  };
  const open = (acc) => call("open", async () => {
    const { data } = await api.post("/client/analytics/pairing", { account: acc }, headers);
    setValue(acc); setPairing(data); refresh();
    if (data.status === "paired") onLinked((await api.post("/client/analytics/check-account", { account: acc }, headers)).data);
    else onLinked(null);
  });
  const test = () => call("test", async () => {
    const { data } = await api.post("/client/analytics/pairing/test", { account: pairing.account }, headers);
    setPairing(data); refresh(); onLinked(data);
  });
  const regenerate = () => {
    if (!window.confirm("Régénérer le token ? L'ancien cessera de fonctionner et il faudra refaire le Pairing.")) return;
    call("regen", async () => {
      setPairing((await api.post("/client/analytics/pairing/regenerate", { account: pairing.account }, headers)).data);
      onLinked(null); refresh();
    });
  };
  const copy = async () => { await navigator.clipboard.writeText(pairing.token); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const remove = () => {
    if (!window.confirm(`Retirer le compte ${pairing.account} ? Son token cessera de fonctionner.`)) return;
    call("remove", async () => {
      await api.delete(`/client/analytics/pairing/${pairing.account}`, headers);
      setPairing(null); setValue(""); onLinked(null); refresh();
    });
  };

  return (
    <>
    <DisconnectedAlert accounts={accounts} />
    <div className="glass rounded-xl p-5 mb-6" data-testid="client-pairing-panel">
      <h2 className="text-sm font-bold mb-1">Compte de trading à analyser</h2>
      <p className="text-xs text-zinc-400 mb-3">Saisissez le numéro de votre compte MT5 pour obtenir son token API.</p>
      <form onSubmit={(e) => { e.preventDefault(); open(value); }} className="flex flex-col sm:flex-row gap-2">
        <input value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))} inputMode="numeric"
               placeholder="ex. 12345678" data-testid="client-account-input"
               className="flex-1 px-3 py-2.5 rounded-lg text-sm bg-black/40 border border-white/10 focus:border-cyan-400/60 outline-none font-mono" />
        <button type="submit" disabled={!!busy || !value} data-testid="client-account-open-btn"
                className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
          {busy === "open" ? "…" : "Valider le compte"}
        </button>
      </form>
      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <span className="text-zinc-500">Vos comptes :</span>
          {accounts.map((a) => (
            <button key={a.account} onClick={() => open(a.account)} data-testid={`client-known-account-${a.account}`}
                    className="px-2.5 py-1 rounded-md bg-white/5 hover:bg-cyan-500/15 font-mono inline-flex items-center gap-1.5">
              {a.account}<span className={`px-1.5 rounded text-[10px] font-sans ${STATUS[a.status]?.[1]}`}>{STATUS[a.status]?.[0]}</span>
            </button>
          ))}
        </div>
      )}
      {pairing && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-4" data-testid="client-pairing-detail">
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Token API du compte <span className="font-mono text-white">{pairing.account}</span></div>
            <div className="flex flex-col sm:flex-row gap-2">
              <code className="flex-1 p-2.5 rounded-lg bg-black/40 font-mono text-xs text-cyan-200 break-all" data-testid="client-pairing-token">{pairing.token}</code>
              <button onClick={copy} className="btn-primary px-4 py-2 rounded-lg text-xs" data-testid="client-pairing-copy">{copied ? "✓ Copié" : "📋 Copier"}</button>
              <button onClick={regenerate} disabled={!!busy} className="btn-ghost px-4 py-2 rounded-lg text-xs" data-testid="client-pairing-regenerate">♻️ Régénérer</button>
            </div>
          </div>
          <Steps account={pairing.account} />
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={test} disabled={!!busy} data-testid="client-pairing-test-btn"
                    className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
              {busy === "test" ? "Test de connexion…" : "🔗 Pairing"}
            </button>
            <span className={`text-xs px-2 py-1 rounded ${STATUS[pairing.status]?.[1]}`} data-testid="client-pairing-status">
              {STATUS[pairing.status]?.[0]}
              {pairing.last_seen_at ? ` · dernière connexion EA ${pairing.last_seen_at.slice(0, 16).replace("T", " ")} UTC` : ""}
            </span>
            <button onClick={remove} disabled={!!busy} className="text-xs text-zinc-500 hover:text-red-400 ml-auto"
                    data-testid="client-pairing-remove">Retirer ce compte</button>
          </div>
        </div>
      )}
      {err && <div className="text-xs text-red-400 mt-3" data-testid="client-pairing-error">{err}</div>}
    </div>
    </>
  );
};
